import { logServiceEvent } from "../../helper/logger";
import { canonicalPackageId } from "../../helper/dynamic-injection/canonical-id";
import { GenreNationalMappingItem } from "../../utils/channelAliases";

export const isNationalForGenre = (localRsnChannel: any, packageKey: string, dma: string, nielsenDma?: string, genreNationalMapping?: any): boolean => {
  try {
    if (!genreNationalMapping || !Array.isArray(genreNationalMapping)) {
      return false;
    }
    const isLocalChannelForNational = genreNationalMapping.find?.(channel => {
      try {
        return (channel?.channel === localRsnChannel?.network || channel?.channel === localRsnChannel?.name);
      } catch {
        return false;
      }
    });
    if (!isLocalChannelForNational) {
      return false;
    }
    const isLocalBelongToNational = isLocalChannelForNational ? ![undefined, null, ""].includes(isLocalChannelForNational?.[packageKey]) : false;
    const dmaValue = dma?.toString?.() || nielsenDma?.toString?.();
    const isNationalChannelInDma = isLocalBelongToNational && isLocalChannelForNational?.[packageKey]?.split?.(",")?.includes?.(dmaValue);
    return !!isNationalChannelInDma;
  } catch {
    // fallback: return false if error occurs
    return false;
  }
};

/**
 * WAT-11611 — local stations come from channels-service; a beam-backed caller
 * falls back to the beam xapi when the service call fails or times out, or for a
 * market the feed lacks (see `pages/api/local-channels.ts` for which callers),
 * and a station's own package list is the source of truth for where
 * it appears: no CMS `genreLocalChannelMapping`, no CMS
 * `signatureExcludedLocalChannels`, no comparison against beam (owner ruling
 * 2026-09-24). The adapter marks those stations (`toLegacyLocalChannels`); a beam
 * station never carries the mark, so the beam fallback keeps the CMS lists.
 */
export const isChannelsServiceLocal = (station: any): boolean => station?.fromChannelsService === true;

// Streaming ids are ATT_TV_*, satellite ids DTV_TV_* or DTV_*, and some pages ask
// with the other product's spelling — so compare the canonical id (prefix stripped).

/** True when a channels-service station lists `packageId` in its own package list. */
export const isServiceLocalInPackage = (station: any, packageId?: string): boolean => {
  const stem = canonicalPackageId(packageId);
  return !!stem && (station?.packageNames ?? [])?.some?.((name: string) => canonicalPackageId(name) === stem);
};

// `packageId` is the genre pack's catalog id (e.g. "ATT_TV_MYSPORTS"). A
// channels-service station is placed by this id alone; without it the station
// joins the genre only through `genreNationalMapping`.
export const handleLocalRsnChannelFilter = (
  localRsnChannels: any,
  genreLocalChannelMapping: any,
  packageKey: string,
  dma: string,
  nielsenDma?: string,
  genreNationalMapping?: any,
  packageId?: string
) => {
  try {
    if (!Array.isArray(localRsnChannels)) {
      return [];
    }
    const filteredChannels = localRsnChannels?.filter(localRsnChannel => {
      try {
        // RSN channels filter for MyHomeTeam genre
        if (packageKey === "myhometeam") {
          const isMyHomeTeamEligible = localRsnChannel?.packageNames?.includes?.("MYHOMETEAM");
          const isMyHomeTeamHasTeams = localRsnChannel?.mlb || localRsnChannel?.nhl || localRsnChannel?.nba || localRsnChannel?.wnba;
          return isMyHomeTeamEligible && isMyHomeTeamHasTeams;
        }
        // Local channels filter for rest of the genres
        if (isChannelsServiceLocal(localRsnChannel)) {
          const inPack = isServiceLocalInPackage(localRsnChannel, packageId);
          const isNationalOnly = !inPack && isNationalForGenre(localRsnChannel, packageKey, dma, nielsenDma, genreNationalMapping);
          if (isNationalOnly) {
            localRsnChannel.isNational = true;
          }
          return inPack || isNationalOnly;
        }
        const isLocalChannel = genreLocalChannelMapping?.find?.(channel => (channel?.channel === localRsnChannel?.network || channel?.channel === localRsnChannel?.name));

        const isLocalBelongToGenre = isLocalChannel ? ![undefined, null, ""]?.includes?.(isLocalChannel?.[packageKey]) : false;
        const isLocalChannelInDma =
          isLocalBelongToGenre &&
          (isLocalChannel?.[packageKey]?.toLowerCase() === "all" || isLocalChannel?.[packageKey]?.split?.(",").includes?.(dma?.toString?.() || nielsenDma?.toString?.()));

        const isNational = isNationalForGenre(localRsnChannel, packageKey, dma, nielsenDma, genreNationalMapping);
        if (isNational && !(isLocalChannel && isLocalBelongToGenre && isLocalChannelInDma)) {
          localRsnChannel.isNational = isNational;
        }
        return (isLocalChannel && isLocalBelongToGenre && isLocalChannelInDma) || isNational;
      } catch {
        // fallback: include the channel if error occurs
        return true;
      }
    });

    return filteredChannels ?? [];
  } catch {
    // fallback: return empty array if error occurs
    return [];
  }
};

// Generated by Copilot- Start
const buildDmaSet = (value: any): Set<string> | null => {
  if (!value || typeof value !== "string") {
    return null;
  }
  const entries = value
    .split?.(",")
    ?.map?.(dma => dma?.trim?.())
    ?.filter?.(dma => !!dma);
  return entries && entries.length ? new Set(entries) : null;
};

const hasLocalChannelForNational = (locals: any[], baseChannel: string): boolean => (Array.isArray(locals) ?
  locals.some(localChannel => {
    const matchesByNetwork = baseChannel === localChannel?.network && !localChannel?.isNational;
    const matchesByLabel = baseChannel === localChannel?.channelNameLabel && localChannel?.categoryId === "Local";
    return matchesByNetwork || matchesByLabel;
  }) :
  false);

const pruneLocalChannels = (locals: any[], baseChannel: string, packageKey: string): void => {
  if (!Array.isArray(locals)) {
    return;
  }
  for (let idx = locals.length - 1; idx >= 0; idx -= 1) {
    const localChannel = locals[idx];
    const isSameChannel = baseChannel === localChannel?.network || baseChannel === localChannel?.channelNameLabel;
    if (!isSameChannel) {
      continue;
    }

    if (localChannel?.categoryId === "National" && Array.isArray(localChannel?.packageListSystem)) {
      localChannel.packageListSystem = localChannel.packageListSystem.filter((pkg: string) => !pkg?.includes?.(packageKey?.toUpperCase?.()));
      continue;
    }

    if (localChannel?.isNational) {
      locals.splice(idx, 1);
    }
  }
};

const pruneNationalChannelsByAlias = (
  channels: any[],
  aliasSet: Set<string>,
  baseChannel: string,
  hasLocalChannel: boolean,
  isDmaEligible: boolean
): any[] => {
  if (!Array.isArray(channels) || !aliasSet.size) {
    return channels;
  }

  const nextChannels: any[] = [];
  let primaryAssigned = false;
  const canNormalizeAliases = aliasSet.size > 1;

  for (const nationalChannel of channels) {
    const channelName = nationalChannel?.channelNameLabel;

    if (!aliasSet.has(channelName)) {
      if (channelName === baseChannel) {
        if (primaryAssigned) {
          continue;
        }
        primaryAssigned = true;
      }
      nextChannels.push(nationalChannel);
      continue;
    }

    if (hasLocalChannel || !isDmaEligible) {
      continue;
    }

    if (!canNormalizeAliases) {
      nextChannels.push(nationalChannel);
      continue;
    }

    if (primaryAssigned) {
      continue;
    }

    primaryAssigned = true;
    nextChannels.push(
      channelName === baseChannel ? nationalChannel : {
        ...nationalChannel,
        channelNameLabel: baseChannel
      }
    );
  }

  return nextChannels;
};

const hasAnyNationalFlags = (flags: any): boolean => !!flags && typeof flags === "object" && Object.keys(flags).length > 0;

const hasLocalChannelForChlup = (channels: any[], baseChannel: string): boolean => {
  if (!Array.isArray(channels)) return false;
  // Detect LOCAL via the structural flag when present (set by buildLocalRsnChannel),
  // falling back to the scalar `category` for any channel that predates the flag.
  // Both resolve identically in Stage 1 (every built LOCAL channel carries both).
  return channels.some(
    (channel) => (channel?.isLocal || channel?.category === "LOCAL") && channel?.name === baseChannel && !hasAnyNationalFlags(channel?.isNational)
  );
};

const pruneChlupChannels = (
  channels: any[],
  aliasSet: Set<string>,
  baseChannel: string,
  {
    hasLocalChannelInDma,
    isNationalChannelInDma,
    categoryKeyLower,
  }: { hasLocalChannelInDma: boolean; isNationalChannelInDma: boolean; categoryKeyLower: string }
): any[] => {
  if (!Array.isArray(channels) || !aliasSet.size) {
    return channels;
  }

  const nextChannels: any[] = [];
  let aliasCount = 0;
  let aliasAssigned = false;
  const canNormalizeAliases = aliasSet.size > 1;

  for (const channel of channels) {
    const channelName = channel?.name;

    if (!aliasSet.has(channelName)) {
      nextChannels.push(channel);
      continue;
    }

    aliasCount += 1;

    if (hasLocalChannelInDma || !isNationalChannelInDma) {
      continue;
    }

    if (!canNormalizeAliases) {
      nextChannels.push(channel);
      continue;
    }

    if (aliasAssigned) {
      continue;
    }

    aliasAssigned = true;
    nextChannels.push(channelName === baseChannel ? channel : {
      ...channel,
      name: baseChannel
    });
  }

  if (!canNormalizeAliases || !aliasCount) {
    return nextChannels;
  }

  return nextChannels.filter(channel => {
    if (!hasAnyNationalFlags(channel?.isNational)) {
      return true;
    }
    return !channel?.isNational?.[categoryKeyLower];
  });
};
// Generated by Copilot- End

export const handleNationalChannelDMAFilter = ({ filteredLocalChannels, nationalChannels, currentDma, nationalMapping, packageKey }) => {
  let filteredNationalChannels = Array.isArray(nationalChannels) ? [...nationalChannels] : [];
  const updatedLocalChannels = Array.isArray(filteredLocalChannels) ? [...filteredLocalChannels] : [];

  try {
    if (!Array.isArray(nationalMapping)) {
      return {
        filteredNationalChannels,
        updatedLocalChannels
      };
    }

    const currentDmaStr = currentDma?.toString?.() ?? "";

    for (const nationalMap of nationalMapping) {
      // Generated by Copilot- Start
      try {
        const aliasList = nationalMap?.channelAliases;
        const baseChannel = nationalMap?.channel;
        if (!baseChannel || !Array.isArray(aliasList) || !aliasList.length) {
          continue;
        }

        const aliasSet = new Set(aliasList.filter(Boolean));
        if (!aliasSet.size) {
          continue;
        }

        const dmaSet = buildDmaSet(nationalMap?.[packageKey]);
        const isNationalChannelInDma = dmaSet ? dmaSet.has(currentDmaStr) : false;
        const isLocalChannelInDma = hasLocalChannelForNational(updatedLocalChannels, baseChannel);

        if (isLocalChannelInDma || isNationalChannelInDma) {
          pruneLocalChannels(updatedLocalChannels, baseChannel, packageKey);
        }

        filteredNationalChannels = pruneNationalChannelsByAlias(
          filteredNationalChannels,
          aliasSet,
          baseChannel,
          isLocalChannelInDma,
          isNationalChannelInDma
        );
      } catch {
        continue;
      }
      // Generated by Copilot- End
    }
  } catch {
    return {
      filteredNationalChannels,
      updatedLocalChannels
    };
  }

  return {
    filteredNationalChannels,
    updatedLocalChannels
  };
};

export const handleNationalLocalDmaFilterChlup = ({ nationalChannels, nationalMapping, packageKey, categoryKey = packageKey, currentDma }: {
  nationalChannels: any[];
  nationalMapping: any[];
  packageKey: string;
  categoryKey?: string;
  currentDma: string | number;
}) => {
  let filteredDmaEligibleChannels = Array.isArray(nationalChannels) ? [...nationalChannels] : [];
  try {
    if (!Array.isArray(nationalMapping)) {
      return filteredDmaEligibleChannels;
    }

    const packageKeyLower = packageKey?.toLowerCase?.() ?? "";
    const categoryKeyLower = categoryKey?.toLowerCase?.() ?? "";
    const currentDmaStr = currentDma?.toString?.() ?? "";

    for (const nationalMap of nationalMapping) {
      try {
        // Generated by Copilot- Start
        const aliasList = nationalMap?.channelAliases;
        const baseChannel = nationalMap?.channel;
        if (!baseChannel || !Array.isArray(aliasList) || !aliasList.length) {
          continue;
        }

        const aliasSet = new Set(aliasList.filter(Boolean));
        if (!aliasSet.size) {
          continue;
        }

        const dmaSet = buildDmaSet(nationalMap?.[packageKeyLower]);
        const isNationalChannelInDma = dmaSet ? dmaSet.has(currentDmaStr) : false;
        const hasLocalChannelInDma = hasLocalChannelForChlup(nationalChannels, baseChannel);

        filteredDmaEligibleChannels = pruneChlupChannels(filteredDmaEligibleChannels, aliasSet, baseChannel, {
          categoryKeyLower,
          hasLocalChannelInDma,
          isNationalChannelInDma
        });
        // Generated by Copilot- End
      } catch (err) {
        // continue to next nationalMap on error
        console.error("handleNationalLocalDmaFilterChlup error", err);
        continue;
      }
    }
  } catch (err) {
    // fallback: return whatever is available
    console.error("handleNationalLocalDmaFilterChlup error", err);
    return filteredDmaEligibleChannels;
  }
  return filteredDmaEligibleChannels;
};

// Generated by Copilot- Start
/**
 * Deduplication Utilities for Local/National Channel Filtering
 * Builds a lookup map for local/RSN channels to identify duplicates with national channels
 * Maps base channel names to their local instances and creates alias-to-base mappings
 * OPTIMIZATION: Only includes aliases for channels that actually exist in local/RSN data
 *
 * @param localChannels - Array of local channels from zipcode API
 * @param rsnChannels - Array of RSN channels from zipcode API
 * @param genreNationalMapping - Mapping array with channel aliases from genreNationals config
 * @returns Object with baseChannelMap (base name -> local channel) and aliasToBaseMap (alias -> base name)
 */
export const buildLocalChannelLookup = (
  localChannels: any[],
  rsnChannels: any[],
  genreNationalMapping?: any[]
): {
  baseChannelMap: Map<string, any>;
  aliasToBaseMap: Map<string, string>;
} => {
  const baseChannelMap = new Map<string, any>();
  const aliasToBaseMap = new Map<string, string>();

  try {
    // Exit early if no mapping data
    if (!genreNationalMapping || !Array.isArray(genreNationalMapping)) {
      return {
        baseChannelMap,
        aliasToBaseMap
      };
    }

    const allLocalRsn = [...(localChannels || []), ...(rsnChannels || [])];
    if (!allLocalRsn.length) {
      return {
        baseChannelMap,
        aliasToBaseMap
      };
    }

    // Build lookup: use display name (network/name/callSign) as used in UI.
    // A channels-service station's network is its raw affiliate ("Univ", not
    // "Univision"), so the CMS genreNationalMapping carries an item under that
    // spelling too (WAT-11611, ACQPROJCI-9078); without it the national stays.
    const localChannelNames = new Set<string>();
    allLocalRsn.forEach(channel => {
      const displayName = channel?.network || channel?.name || channel?.callSign;
      if (displayName) {
        baseChannelMap.set(displayName, channel);
        localChannelNames.add(displayName);
      }
    });

    // OPTIMIZATION: Build alias-to-base mapping ONLY for channels that exist in local/RSN data
    // This prevents unnecessary filtering iterations for channels not in the DMA
    let includedMappings = 0;

    genreNationalMapping.forEach(mapping => {
      const baseChannel = mapping?.channel;
      const aliases = mapping?.channelAliases;

      if (!baseChannel || !Array.isArray(aliases) || !aliases.length) {
        return;
      }

      // OPTIMIZATION: Only include this mapping if the base channel exists in local/RSN data
      if (!localChannelNames.has(baseChannel)) {
        return; // Skip this mapping - no local channel present
      }

      // Include aliases for this channel
      aliases.forEach(alias => {
        if (alias) {
          aliasToBaseMap.set(alias, baseChannel);
        }
      });
      includedMappings += 1;
    });

    logServiceEvent("Channels", "buildLocalChannelLookup", {
      localRsnCount: baseChannelMap.size,
      aliasCount: aliasToBaseMap.size,
      mappingsUsed: includedMappings
    });
  } catch (error) {
    console.error("buildLocalChannelLookup error:", error);
  }

  return {
    baseChannelMap,
    aliasToBaseMap
  };
};

/**
 * Determines if a national channel should be removed or have signature packages stripped
 * Based on local channel presence and DMA eligibility for genre packs
 *
 * @param nationalChannel - The national channel to check (uses label/displayText/channelNameLabel)
 * @param localChannelLookup - Lookup maps from buildLocalChannelLookup
 * @param dmaData - Current DMA information
 * @param genreLocalMapping - Genre-to-DMA eligibility mapping
 * @param genreNationalMapping - National channel aliases mapping
 * @param packageSummaries - Package summaries for genre pack identification (optional, for non-genre variants)
 * @returns { remove: boolean, stripSignaturePackages: boolean }
 */
export const shouldRemoveNationalChannel = (
  nationalChannel: any,
  localChannelLookup: { baseChannelMap: Map<string, any>; aliasToBaseMap: Map<string, string> },
  dmaData: { dma?: string; nielsenDma?: string } | null,
  genreLocalMapping?: any[],
  genreNationalMapping?: any[],
  packageSummaries?: any
): { remove: boolean; stripSignaturePackages: boolean } => {
  try {
    // Exit early if no mapping data
    if (!genreNationalMapping || !Array.isArray(genreNationalMapping)) {
      return {
        remove: false,
        stripSignaturePackages: false
      };
    }

    const { baseChannelMap, aliasToBaseMap } = localChannelLookup;

    // Get national channel display name (as shown in UI)
    const nationalDisplayName = nationalChannel?.label || nationalChannel?.displayText || nationalChannel?.channelNameLabel || nationalChannel?.name;
    if (!nationalDisplayName) {
      return {
        remove: false,
        stripSignaturePackages: false
      };
    }

    // Check if this national channel is an alias
    const baseChannelName = aliasToBaseMap.get(nationalDisplayName);
    if (!baseChannelName) {
      // Not an alias, no action needed
      return {
        remove: false,
        stripSignaturePackages: false
      };
    }

    // Check if local/RSN channel exists for this base name
    const localOrRsnChannel = baseChannelMap.get(baseChannelName);
    if (!localOrRsnChannel) {
      // No local/RSN channel, keep national
      return {
        remove: false,
        stripSignaturePackages: false
      };
    }

    // WAT-11611: a channels-service local is in a genre pack only if its own list says so.
    // ponytail: needs packageSummaries to know which packs are genre; without them the CMS
    // path below still runs.
    if (isChannelsServiceLocal(localOrRsnChannel) && packageSummaries) {
      const inAnyGenre = Object.values(packageSummaries)
        ?.some?.((pkg: any) => (pkg?.type === "genre" || pkg?.type === "genre-mini") && isServiceLocalInPackage(localOrRsnChannel, pkg?.id));
      return {
        remove: !!inAnyGenre,
        stripSignaturePackages: !inAnyGenre
      };
    }

    // Local/RSN channel exists - now check DMA eligibility for genre packs
    const dma = dmaData?.dma || "";
    const nielsenDma = dmaData?.nielsenDma;

    if (!genreLocalMapping || !Array.isArray(genreLocalMapping)) {
      // No genre mapping - remove national entirely (local/RSN covers all signature packages)
      return {
        remove: true,
        stripSignaturePackages: false
      };
    }

    // Find local/RSN channel's genre eligibility
    const localGenreMapping = genreLocalMapping.find(mapping => mapping?.channel === baseChannelName);

    if (!localGenreMapping) {
      // Local/RSN not mapped to any genre - remove national entirely
      return {
        remove: true,
        stripSignaturePackages: false
      };
    }

    // Check DMA eligibility across all genre packages
    let isEligibleForAnyGenre = false;
    const genrePackageKeys = packageSummaries ?
      Object.values(packageSummaries)
        .filter((pkg: any) => pkg?.type === "genre" || pkg?.type === "genre-mini")
        .map((pkg: any) => pkg?.queryParam) :
      Object.keys(localGenreMapping).filter(key => key !== "channel");

    for (const packageKey of genrePackageKeys) {
      const dmaList = localGenreMapping[packageKey];
      if (!dmaList || dmaList === "" || dmaList === null || dmaList === undefined) {
        continue;
      }

      // Check if DMA is eligible
      const isEligible =
        dmaList.toLowerCase() === "all" ||
        dmaList.split(",").some((d: string) => d.trim() === dma || d.trim() === nielsenDma);

      if (isEligible) {
        isEligibleForAnyGenre = true;
        break;
      }
    }

    if (isEligibleForAnyGenre) {
      // Local/RSN is eligible for genre packs - remove national entirely
      return {
        remove: true,
        stripSignaturePackages: false
      };
    }
    // Local/RSN NOT eligible for genre packs - keep national but strip signature packages
    // National should only show genre packages (avoid duplicate signature packages)
    return {
      remove: false,
      stripSignaturePackages: true
    };
  } catch (error) {
    console.error("shouldRemoveNationalChannel error:", error);
    return {
      remove: false,
      stripSignaturePackages: false
    };
  }
};

/**
 * Filters an array of channels to remove national duplicates based on local channel availability
 * Optionally strips signature packages from nationals that should only show genre packs
 *
 * @param channels - Array of channels (can be ChannelCardV2[] or normalized string[])
 * @param localChannelLookup - Lookup maps from buildLocalChannelLookup
 * @param channelMap - Channel map for denormalization (if channels are string[])
 * @param dmaData - Current DMA information
 * @param genreLocalMapping - Genre-to-DMA eligibility mapping
 * @param genreNationalMapping - National channel aliases mapping
 * @param packageSummaries - Package summaries (optional, for non-genre variants)
 * @returns Filtered channel array (maintains same type as input)
 */
export const filterDuplicateNationalChannels = (
  channels: any[],
  localChannelLookup: { baseChannelMap: Map<string, any>; aliasToBaseMap: Map<string, string> },
  channelMap: Record<string, any> | undefined,
  dmaData: { dma?: string; nielsenDma?: string } | null,
  genreLocalMapping?: any[],
  genreNationalMapping?: any[],
  packageSummaries?: any
): any[] => {
  try {
    if (!Array.isArray(channels) || !channels.length) {
      return channels;
    }

    // Exit early if no mapping data
    if (!genreNationalMapping || !Array.isArray(genreNationalMapping)) {
      return channels;
    }

    const { aliasToBaseMap, baseChannelMap } = localChannelLookup;

    // OPTIMIZATION: Skip filtering if no local channels exist
    // BUT still filter if baseChannelMap has entries (for RSN direct name matches)
    if (aliasToBaseMap.size === 0 && baseChannelMap.size === 0) {
      return channels;
    }

    // Check if channels are normalized (string[]) or denormalized (object[])
    const isNormalized = typeof channels[0] === "string";

    const filteredChannels: any[] = [];
    let removedCount = 0;
    let strippedCount = 0;
    let directMatchRemovals = 0;

    for (const channel of channels) {
      let channelObj: any;
      let channelId: string | undefined;

      if (isNormalized) {
        // Normalized - lookup in channelMap
        channelId = channel as string;
        channelObj = channelMap?.[channelId];
        if (!channelObj) {
          // Keep if not found in map
          filteredChannels.push(channel);
          continue;
        }
      } else {
        // Already denormalized
        channelObj = channel;
      }

      // Get channel display name
      const displayName = channelObj?.label || channelObj?.displayText || channelObj?.channelNameLabel || channelObj?.name;

      // OPTIMIZATION 1: Check if there's a direct name match with any local/RSN channel
      // This handles RSN channels that don't have aliases in genreNationalMapping
      const directMatch = baseChannelMap.get(displayName);
      if (directMatch) {
        // Found a local/RSN channel with exact same name - remove this national
        removedCount += 1;
        directMatchRemovals += 1;
        continue;
      }

      // OPTIMIZATION 2: Quick check if this channel is in the alias map
      const baseChannelName = aliasToBaseMap.get(displayName);

      if (!baseChannelName) {
        // Not an alias and no direct match, keep channel immediately
        filteredChannels.push(channel);
        continue;
      }

      // This is an alias - now do the full check
      const { remove, stripSignaturePackages } = shouldRemoveNationalChannel(
        channelObj,
        localChannelLookup,
        dmaData,
        genreLocalMapping,
        genreNationalMapping,
        packageSummaries
      );

      if (remove) {
        // Skip this channel (remove from list)
        removedCount += 1;
        continue;
      }

      if (stripSignaturePackages && !isNormalized) {
        // Strip signature packages in-place (only for denormalized objects)
        const strippedChannel = {
          ...channelObj,
          signaturePackages: [],
          hasIncludedPackages: (channelObj?.genrePackages?.length || 0) > 0
        };
        filteredChannels.push(strippedChannel);
        strippedCount += 1;
      } else {
        // Keep as-is
        filteredChannels.push(channel);
      }
    }

    // Only log if there were actual removals or stripping
    if (removedCount > 0 || strippedCount > 0) {
      logServiceEvent("Channels", "filterDuplicateNationalChannels", {
        total: channels.length,
        removed: removedCount,
        directMatches: directMatchRemovals,
        stripped: strippedCount,
        final: filteredChannels.length
      });
    }

    return filteredChannels;
  } catch (error) {
    console.error("filterDuplicateNationalChannels error:", error);
    return channels;
  }
};
// Generated by Copilot- End

/**
 * The ONE place that decides whether `signatureExcludedLocalChannels` applies to a surface.
 *
 * It is a STREAM-only product rule: WAT-10364 scoped it to "DIRECTV via Internet signature
 * packages" and named only the stream surfaces in its AC; WAT-11768 confirmed satellite must
 * ignore it entirely (a satellite market was hiding its real local Telemundo affiliate).
 *
 * Satellite gets `undefined`, which every consumer below already reads as "no exclusions"
 * (`!Array.isArray(...) -> false`), so satellite degrades to pre-WAT-10364 behaviour with no
 * per-call-site branching. Call this at the ONE point each surface reads the config; never
 * re-implement the check inline, or a future surface will silently opt itself back in.
 *
 * ONE documented exception: a surface that renders satellite AND stream locals in a single merged
 * list cannot answer "is this surface satellite?" at all, because a row can be on both. That case
 * uses `demoteStreamExcludedLocals` below instead, which downgrades the row's stream availability
 * and never touches `isSatellite`. Do not "restore" this function at that call site - doing so is
 * what caused WAT-11773 (the dual-feed row exempted itself and rendered as carried on both).
 *
 * @param signatureExcludedLocalChannels - the raw CMS array (may be undefined)
 * @param isSatellite - true when the calling surface is rendering the satellite flow
 */
export const signatureExclusionsForSurface = (
  signatureExcludedLocalChannels?: any[],
  isSatellite?: boolean
): any[] | undefined => (isSatellite ? undefined : signatureExcludedLocalChannels);

// Generated by Copilot- Start - 2026-04-23 09:00:00
/**
 * Checks if a local channel should be excluded from a signature package for a given DMA.
 * Reads from `signatureExcludedLocalChannels` CMS config (Storyblok block: signatureExcludedLocalChannels).
 *
 * Hybrid lookup (scales to 100+ DMAs):
 *   1. Blanket — DMA in `blanketDmas` → excluded from ALL signature pkgs for that channel
 *   2. Narrow  — DMA-specific entry in `perDmaOverrides` with `excludeFromPackages` listing pkgs to hide
 *
 * Authoring shape:
 *   { channel: "Telemundo",
 *     blanketDmas: "820,635,819,801",
 *     perDmaOverrides: [
 *       { dma: "908", excludeFromPackages: "ATT_TV_ENTERTAINMENT,ATT_TV_CHOICE,ATT_TV_ULTIMATE" }
 *     ] }
 *
 * @param channelNetwork - Channel's network (matches xapi response e.g. "Telemundo")
 * @param dma            - User's DMA code
 * @param signatureExcludedLocalChannels - Array from Storyblok CMS config
 * @param packageId      - Optional signature package ID. Required for narrow-override resolution.
 * @param station        - The station itself. A channels-service station skips the CMS config:
 *                         it is excluded exactly from the packages its own list omits.
 * @returns true if the channel should be hidden from the (optionally specified) signature package
 */
export const isLocalChannelExcludedFromSignature = (
  channelNetwork: string,
  dma: string,
  signatureExcludedLocalChannels?: any[],
  packageId?: string,
  station?: any
): boolean => {
  try {
    if (isChannelsServiceLocal(station)) {
      // No package named = "drop the row entirely?". A service station is in the market's
      // list only because it is carried, so the answer is no. This is NOT "included in every
      // package" — callers that tick package columns must read the station's own list.
      return !!packageId && !isServiceLocalInPackage(station, packageId);
    }
    if (!Array.isArray(signatureExcludedLocalChannels) || !channelNetwork || !dma) {
      return false;
    }

    const entry = signatureExcludedLocalChannels.find(e => e?.channel === channelNetwork);
    if (!entry) {
      return false;
    }

    const dmaStr = dma.toString().trim();

    // 1. Blanket fast-path — `blanketDmas` is a Storyblok textarea: CSV / newlines / mixed
    if (entry.blanketDmas) {
      const list = Array.isArray(entry.blanketDmas) ?
        entry.blanketDmas :
        String(entry.blanketDmas).split(/[,\s]+/);
      if (list.map((d: any) => d?.toString().trim()).filter(Boolean).includes(dmaStr)) {
        return true;
      }
    }

    // 2. Per-DMA narrow override
    const override = Array.isArray(entry.perDmaOverrides) ?
      entry.perDmaOverrides.find((o: any) => o?.dma?.toString().trim() === dmaStr) :
      undefined;
    if (!override?.excludeFromPackages) {
      return false;
    }

    if (!packageId) {
      return false;
    } // partial exclusion — let per-package consumers re-check
    const excludedPkgs = override.excludeFromPackages.split(",").map((p: string) => p.trim());
    return excludedPkgs.includes(packageId);
  } catch {
    return false;
  }
};

/**
 * Returns the list of signature packages a local channel should appear in,
 * after removing any packages excluded by the CMS config. A channels-service
 * station skips the CMS config and keeps only the packages its own list names.
 */
export const getSignaturePackagesForLocalChannel = (
  channelNetwork: string,
  dma: string,
  allSignaturePackages: string[],
  signatureExcludedLocalChannels?: any[],
  station?: any
): string[] => {
  try {
    if (!Array.isArray(allSignaturePackages)) {
      return allSignaturePackages;
    }
    if (isChannelsServiceLocal(station)) {
      return allSignaturePackages.filter(pkgId => isServiceLocalInPackage(station, pkgId));
    }
    if (!Array.isArray(signatureExcludedLocalChannels) || signatureExcludedLocalChannels.length === 0) {
      return allSignaturePackages;
    }
    return allSignaturePackages.filter(
      pkgId => !isLocalChannelExcludedFromSignature(channelNetwork, dma, signatureExcludedLocalChannels, pkgId)
    );
  } catch {
    return allSignaturePackages;
  }
};

/**
 * Filters out local channels that are excluded from a signature package for the given DMA.
 * Pass `packageId` to apply per-package narrow rules; omit to filter only on blanket exclusion.
 */
export const filterSignatureExcludedLocalChannels = (
  localChannels: any[],
  dma: string,
  signatureExcludedLocalChannels?: any[],
  packageId?: string
): any[] => {
  try {
    if (!Array.isArray(localChannels)) {
      return localChannels;
    }
    // A channels-service station is judged by its own package list even when the
    // CMS has nothing to say, so only a beam-only list may skip the filter.
    if (!(signatureExcludedLocalChannels?.length > 0) && !localChannels.some(isChannelsServiceLocal)) {
      return localChannels;
    }
    return localChannels.filter(channel => {
      const network = channel?.network || channel?.name || channel?.channelNameLabel || channel?.label;
      return !isLocalChannelExcludedFromSignature(network, dma, signatureExcludedLocalChannels, packageId, channel);
    });
  } catch {
    return localChannels;
  }
};
// Generated by Copilot- End - 2026-04-23 09:00:00

/**
 * The stream-only signature rule does not REMOVE a local from a merged satellite+stream list —
 * it means that local is not carried on STREAM in this DMA. [WAT-11773]
 *
 * So on the one surface that merges both feeds (local-rsn-tool), demote the stream availability
 * of the row rather than deleting the row:
 *   - a local on BOTH feeds becomes satellite-only, which is what that tool's
 *     "Exclusively on Satellite" banner and its STREAM view filter both already read;
 *   - a local that was stream-ONLY ends up available nowhere and is dropped, preserving
 *     WAT-10364's behaviour;
 *   - a satellite-only local, an RSN row, and any channel the config does not name are untouched.
 *
 * Deleting every matching row instead is what hid satellite Telemundo in 96 markets (WAT-11768),
 * and exempting the dual-feed row is what made it read as available on both (WAT-11773).
 */
export const demoteStreamExcludedLocals = (
  channels: any[],
  dma: string,
  signatureExcludedLocalChannels?: any[]
): any[] => {
  try {
    if (!Array.isArray(channels)) {
      return channels;
    }
    return channels
      .map(channel => {
        if (channel?.TYPE !== "LOCAL") {
          return channel;
        }
        const carriedOnStream = filterSignatureExcludedLocalChannels(
          [channel],
          dma,
          signatureExcludedLocalChannels
        )?.length > 0;
        if (carriedOnStream) {
          return channel;
        }
        return {
          ...channel,
          isStream: false
        };
      })
      .filter(channel => channel?.TYPE !== "LOCAL" || channel?.isSatellite || channel?.isStream);
  } catch {
    return channels;
  }
};

/**
 * The product view a `channelsQP` value asks for, or `undefined` for "no view - keep the merged
 * list". [WAT-11773]
 *
 * `"INTERNET"` is the stream product's own spelling on these surfaces - it is what
 * `channel-compare-tool-page-adapter.ts` DEFAULTS to for a lineup page, and what
 * `channel-compare-tool-v3` and its own merged local/RSN list already treat as stream. Reading it
 * as "unrecognised" here would let a stream-excluded local survive an internet-only view and then
 * render an "Exclusively on Satellite" banner on a page that sells no satellite.
 */
export const productViewFromChannelsQP = (
  channelsQP?: string
): "STREAM" | "SATELLITE" | undefined => {
  const view = channelsQP?.toUpperCase?.();
  if (view === "STREAM" || view === "INTERNET") {
    return "STREAM";
  }
  if (view === "SATELLITE") {
    return "SATELLITE";
  }
  return undefined;
};

/**
 * The merged-list pipeline for local-rsn-tool, exported as ONE step so the ORDER is testable
 * without mounting that component. [WAT-11773]
 *
 * Demote FIRST, then narrow to the requested product view. That order IS the fix: filtering
 * first would let a dual-feed local survive a STREAM view (its isStream is still true at that
 * point) and only then be stamped satellite-only - rendering a channel that is NOT carried on
 * stream inside the stream-only view, "Exclusively on Satellite" banner and all.
 *
 * A `channelsQP` that names no product view leaves the merged list as-is.
 */
export const demoteThenFilterForView = (
  channels: any[],
  dma: string,
  signatureExcludedLocalChannels?: any[],
  channelsQP?: string
): any[] => {
  const demoted = demoteStreamExcludedLocals(channels, dma, signatureExcludedLocalChannels);
  const view = productViewFromChannelsQP(channelsQP);
  if (view === "STREAM") {
    return demoted?.filter(channel => channel?.isStream);
  }
  if (view === "SATELLITE") {
    return demoted?.filter(channel => channel?.isSatellite);
  }
  return demoted;
};

/**
 * The whole local + RSN row pipeline for local-rsn-tool: merge the two product feeds, collapse
 * duplicates, apply the stream-only signature rule as a carriage demotion, then narrow to the
 * requested product view. [WAT-11773]
 *
 * This lives here rather than inline in the component for one reason: the component has no spec,
 * so anything left in it is untestable - and every regression on this rule so far (WAT-11768, then
 * WAT-11773) has been a defect of ORDER, not of arithmetic. The de-dup marks a channel returned by
 * BOTH feeds as carried on both; the demotion must see that row, and the view filter must run after
 * the demotion. Keeping all four steps in one tested function is what makes those orders provable.
 *
 * Rows are tagged by the caller (`isStream`/`isSatellite`/`TYPE`); the merge order decides which
 * feed's metadata wins a duplicate, and it follows the same view as the narrowing.
 */
export const buildLocalRsnRows = (
  satelliteRows: any[],
  streamRows: any[],
  dma: string,
  signatureExcludedLocalChannels?: any[],
  channelsQP?: string
): any[] => {
  const merged = productViewFromChannelsQP(channelsQP) === "STREAM" ?
    [...(satelliteRows || []), ...(streamRows || [])] :
    [...(streamRows || []), ...(satelliteRows || [])];

  const unique: Record<string, any> = {};
  for (const channel of merged) {
    const key = `${channel?.callSign} ${channel?.network} ${channel?.channelNumber}`;
    unique[key] = unique[key] ?
      {
        ...channel,
        isSatellite: true,
        isStream: true
      } :
      channel;
  }

  return demoteThenFilterForView(
    Object.keys(unique).map(key => unique[key]),
    dma,
    signatureExcludedLocalChannels,
    channelsQP
  );
};

/**
 * Agent-tool "All Channels" DMA gate.
 *
 * The agent package-compare tool builds its "All Channels" list by concatenating the
 * channel-service master feed and the beam locals with NO per-DMA gate — unlike the genre
 * sections, which run `handleNationalChannelDMAFilter`. That leaves two artifacts in markets
 * where a channel is NOT its national market:
 *   1. A master-feed NATIONAL channel (a `genreNationals` base/alias, e.g. "Telemundo East")
 *      surfaces in every DMA, even blocked/local ones.
 *   2. The beam's redundant pseudo-local of the same alias surfaces alongside it, tagged "Local".
 *
 * This filter — applied to the formatted All-Channels rows ({ channelName, category }) — mirrors
 * that gate for every `genreNationals`-tracked channel (Telemundo, NBC, Univision, ...), reading
 * each entry's per-genre DMA lists by the SAME known genre queryparams the genre sections use
 * (`mapping[queryparam]`), never by sniffing fields:
 *   • A master-feed national channel (non-LOCAL) is kept only where the current DMA appears in ANY
 *     of that entry's per-genre DMA lists (its national markets); it is then RENAMED from its alias
 *     to the base `channel` and deduped, so a national market shows ONE base-named row (e.g.
 *     "Telemundo", not two "Telemundo East"). Otherwise it is dropped.
 *   • A beam LOCAL channel whose name is a `genreNationals` ALIAS (not a base `channel`) is the
 *     national pseudo-local — always dropped, since the master feed carries the national copy.
 *   • The real local affiliate (a LOCAL channel named by a base `channel`) is left untouched, as
 *     are channels not tracked by `genreNationals`.
 *
 * @param genreQueryParams genre queryparams (from packageConfig via getGenreMapping) = the DMA-list keys.
 */
export const filterAllChannelsNationalByDma = (
  channels: any[],
  genreNationalMapping: GenreNationalMappingItem[] | undefined,
  dma: string,
  genreQueryParams: string[]
): any[] => {
  try {
    if (
      !Array.isArray(channels) ||
      !Array.isArray(genreNationalMapping) ||
      genreNationalMapping.length === 0 ||
      !Array.isArray(genreQueryParams) ||
      genreQueryParams.length === 0
    ) {
      return channels;
    }
    const dmaStr = dma?.toString?.().trim?.() ?? "";
    if (!dmaStr) {
      return channels; // DMA unavailable — cannot gate; leave the list untouched (fail-open)
    }
    // any tracked channel name (base or alias) -> its base channel
    const baseByName = new Map<string, string>();
    // channel name (base or alias) -> national eligibility (union of the entry's per-genre DMA lists)
    const eligibilityByName = new Map<string, { anyDma: boolean; dmas: Set<string> }>();

    for (const mapping of genreNationalMapping) {
      const base = mapping?.channel;
      if (!base) {
        continue;
      }
      const dmas = new Set<string>();
      let anyDma = false;
      // Read only the known genre queryparam keys — exactly how handleNationalChannelDMAFilter
      // reads mapping[packageKey]. The union across genres is this channel's national-market set.
      for (const queryparam of genreQueryParams) {
        const value = mapping?.[queryparam];
        if (typeof value !== "string") {
          continue;
        }
        if (value.trim().toLowerCase() === "all") {
          anyDma = true;
          continue;
        }
        buildDmaSet(value)?.forEach(d => dmas.add(d));
      }
      const eligibility = {
        anyDma,
        dmas
      };
      baseByName.set(base, base);
      eligibilityByName.set(base, eligibility);
      // Read BOTH field names: this feed may be arraytoObj-normalized (`channelAliases`) OR raw
      // Storyblok (`array_channelAliases`). Guard the shape so a non-array value can't throw and,
      // via the outer catch, silently disable the whole gate.
      const aliasList = mapping?.channelAliases ?? mapping?.array_channelAliases;
      const aliases = Array.isArray(aliasList) ? aliasList.filter(Boolean) : [];
      for (const alias of aliases) {
        baseByName.set(alias, base);
        eligibilityByName.set(alias, eligibility);
      }
    }

    // Rename alias -> base + dedup, mirroring pruneNationalChannelsByAlias (the genre / channel-lineup
    // path), so the All-Channels list shows ONE base-named national in a national market (e.g. a
    // single "Telemundo", not two "Telemundo East" rows).
    const seenNationalBase = new Set<string>();
    const result: any[] = [];
    for (const chnl of channels) {
      const name = chnl?.channelName ?? chnl?.channelNameLabel ?? chnl?.network ?? chnl?.name;
      const base = baseByName.get(name);
      if (base === undefined) {
        result.push(chnl); // not tracked by genreNationals — leave it alone
        continue;
      }
      const isLocal = String(chnl?.category ?? chnl?.categoryId ?? "").toLowerCase() === "local";
      if (isLocal) {
        // Real local affiliate is named by the base channel and stays; the beam's national
        // pseudo-local is named by an ALIAS and is dropped (the master feed carries the national).
        if (name === base) {
          result.push(chnl);
        }
        continue;
      }
      // Master-feed national: keep only in its national markets, renamed to the base, deduped.
      const eligibility = eligibilityByName.get(name);
      if (!eligibility || !(eligibility.anyDma || eligibility.dmas.has(dmaStr))) {
        continue;
      }
      if (seenNationalBase.has(base)) {
        continue;
      }
      seenNationalBase.add(base);
      result.push(name === base ? chnl : {
        ...chnl,
        channelName: base,
        channelNameLabel: base,
        name: base
      });
    }
    return result;
  } catch {
    return channels;
  }
};


/**
 * Hide a channel from the currently-displayed package columns when signatureExcluded says so —
 * covering BOTH the included tiers AND the add-on rows.
 *
 * These compare tools display only English packages. `signatureExcludedLocalChannels` says which
 * packages Telemundo is excluded from per DMA: `blanketDmas` = every package (blocked market);
 * `perDmaOverrides.excludeFromPackages` = the listed package IDs (e.g. the 4 English tiers for an
 * Óptimo-Más national market like 518). A channel excluded from EVERY currently-displayed package
 * has nothing to show here, so drop it — the grid's master filter would drop an all-empty channel
 * anyway. Dropping removes both the ✓ (included) and the $ (add-on) for those columns.
 *
 * So on 518 (override lists all 4 displayed English tiers) → dropped (no ✓, no $). On a DMA whose
 * override does NOT list the displayed packages — or a local-feed market NOT in the overrides at all
 * (e.g. 623) → at least one displayed package is not excluded → kept (local affiliate shows).
 *
 * Alias-aware: config keyed by base ("Telemundo"), feed row may be an alias ("Telemundo East");
 * `genreNationalMapping` resolves alias→base. Uses existing config — no CMS change. [WAT-11301]
 */
export const filterChannelsExcludedFromDisplayedPackages = (
  channels: any[],
  dma: string,
  signatureExcludedLocalChannels?: any[],
  displayedPackageIds?: string[],
  genreNationalMapping?: GenreNationalMappingItem[]
): any[] => {
  try {
    if (!Array.isArray(channels)) {
      return channels;
    }
    const pkgIds = Array.isArray(displayedPackageIds) ? displayedPackageIds.filter(Boolean) : [];
    const hasConfig = Array.isArray(signatureExcludedLocalChannels) && signatureExcludedLocalChannels.length > 0;
    if (!hasConfig && !channels.some(isChannelsServiceLocal)) {
      return channels;
    }
    const aliasToBase = new Map<string, string>();
    if (Array.isArray(genreNationalMapping)) {
      for (const mapping of genreNationalMapping) {
        const base = mapping?.channel;
        if (!base) {
          continue;
        }
        aliasToBase.set(base, base);
        const aliasList = mapping?.channelAliases ?? mapping?.array_channelAliases;
        if (Array.isArray(aliasList)) {
          for (const alias of aliasList) {
            if (alias) {
              aliasToBase.set(alias, base);
            }
          }
        }
      }
    }
    return channels.filter(channel => {
      const name = channel?.network || channel?.name || channel?.channelNameLabel || channel?.label || channel?.channelName;
      if (!name) {
        return true;
      }
      const base = aliasToBase.get(name) ?? name;
      // Match the raw feed name OR its resolved base against the config.
      const excludedFrom = (packageId?: string) => isLocalChannelExcludedFromSignature(name, dma, signatureExcludedLocalChannels, packageId, channel) ||
        isLocalChannelExcludedFromSignature(base, dma, signatureExcludedLocalChannels, packageId, channel);
      // Blanket (no packageId) → excluded from everything → drop.
      if (excludedFrom()) {
        return false;
      }
      // Columns unknown → only blanket applies (handled above) → keep.
      if (pkgIds.length === 0) {
        return true;
      }
      // Per-package: keep only if at least one displayed package is NOT excluded.
      return pkgIds.some(packageId => !excludedFrom(packageId));
    });
  } catch {
    return channels;
  }
};
